/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { StreamDeviceTypes } from '../middlewares/devicesMiddleware/StreamManager';
import CameraEffects from './CameraEffects';
export default class StreamMerger {
  constructor(frameRate) {
    this.frameRate = frameRate;
    this.cameraEffects = new CameraEffects();
  }
  registerCanvas(canvas) {
    this.externalCanvas = canvas;
  }
  setCameraEffects(effects) {
    this.cameraEffects.setCameraEffects(effects);
  }
  getStream() {
    return this.outputStream;
  }
  start() {
    if (this.outputStream) return;

    // it seems we have system limits on amount of contexts we can open
    // "It's recommended to create one AudioContext and reuse it instead of initializing a new one each time" MDN
    this.audioCtx = new AudioContext();
    this.outputStream = new MediaStream();
    this.emptyStream = this.createEmptyVideoStream();
    // we don't want to have any bit of empty preview
    // so we fill it with fake data from placeholder canvas
    this.emptyStream.getTracks().forEach(track => this.outputStream.addTrack(track));
  }
  stopWebcamStream() {
    if (this.webcamStream) {
      this.webcamStream.getTracks().forEach(track => track.stop());
      this.webcamStream = void 0;
    }
  }
  stop() {
    if (!this.outputStream) return;
    this.stopWebcamStream();

    // we only stop tracks that were created by this class
    // other tracks are managed by the stream manager
    this.emptyStream.getTracks().forEach(track => track.stop());
    this.emptyStream = void 0;
    this.outputStream.getAudioTracks().forEach(track => track.stop());
    this.outputStream.getTracks().forEach(track => this.outputStream.removeTrack(track));
    this.outputStream = void 0;
    this.stopOscillator();
    this.audioCtx.close();
    this.audioCtx = void 0;
  }
  async mergeStreams(streamsWithMeta) {
    if (!this.outputStream) {
      // in case we close component before we initiate preview
      // we need to stop initialized tracks
      streamsWithMeta.forEach(({
        stream
      }) => {
        stream.getTracks().forEach(track => track.stop());
      }, []);
      return;
    }
    this.stopWebcamStream();
    const mergedVideoTracks = await this.mergeVideoStreams(streamsWithMeta);
    const mergedAudioTracks = this.mergeAudioStreams(streamsWithMeta);
    const oldTracks = this.outputStream.getTracks();
    oldTracks.forEach(track => this.outputStream.removeTrack(track));
    const tracks = [...mergedVideoTracks, ...mergedAudioTracks];
    tracks.forEach(track => this.outputStream.addTrack(track));
  }
  async mergeVideoStreams(streamsWithMeta) {
    const videoStreamsWithMeta = [];
    streamsWithMeta.forEach(({
      stream,
      videoConfig,
      type
    }) => {
      if (stream.getVideoTracks().length) {
        videoStreamsWithMeta.push({
          stream,
          config: videoConfig,
          type
        });
      }
    }, []);
    let resultTracks;
    // the first stream is always the screen sharing stream,
    // or the webcam stream if screen sharing is not active
    // so we only ever use the first steam here, as if there are multiple streams,
    // the webcam would either be disabled or in picture-in-picture mode
    if (videoStreamsWithMeta[0]) {
      if (videoStreamsWithMeta[0].type === StreamDeviceTypes.Webcam) {
        const newStream = await this.cameraEffects.start(videoStreamsWithMeta[0].stream);
        resultTracks = newStream.getVideoTracks();
      } else {
        resultTracks = videoStreamsWithMeta[0].stream.getVideoTracks();
      }
    } else {
      // no stream is active, we need to use the empty stream
      resultTracks = this.emptyStream.getVideoTracks();
    }
    if (videoStreamsWithMeta[1]) {
      this.webcamStream = await this.cameraEffects.start(videoStreamsWithMeta[1].stream);
    }
    return resultTracks;
  }
  mergeAudioStreams(streamsWithMeta) {
    // we don't need old oscillator running when mergeAudioStreams is called
    // as we will create a new one if needed
    this.stopOscillator();
    const output = this.audioCtx.createMediaStreamDestination();
    let hasAudioStream = false;
    streamsWithMeta.forEach(({
      stream,
      audioConfig
    }) => {
      if (!audioConfig.muted && stream.getAudioTracks().length > 0) {
        hasAudioStream = true;
        const source = this.audioCtx.createMediaStreamSource(stream);
        const gain = this.audioCtx.createGain();
        gain.gain.value = audioConfig.gain;
        source.connect(gain).connect(output);
      }
    });
    return hasAudioStream ? output.stream.getAudioTracks() : this.createSilentAudioStream().getAudioTracks(); // firefox can't record correct webm videos without audio track
  }
  createEmptyVideoStream() {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('width', 1280);
    canvas.setAttribute('height', 720);
    const canvasContext = canvas.getContext('2d', {
      willReadFrequently: true
    });
    canvasContext.fillStyle = 'black';
    canvasContext.fillRect(0, 0, 1280, 720);
    return canvas.captureStream(this.frameRate);
  }
  createSilentAudioStream() {
    this.oscillatorNode = new OscillatorNode(this.audioCtx, {
      frequency: 0 // no frequency means silent
    }); // audio source node that generates audio wave of some frequency
    const streamAudioDestination = new MediaStreamAudioDestinationNode(this.audioCtx); // audio destination consisting of MediaStream with a single AudioMediaStreamTrack

    this.oscillatorNode.connect(streamAudioDestination);
    this.oscillatorNode.start();
    return streamAudioDestination.stream;
  }
  stopOscillator() {
    if (this.oscillatorNode) {
      // when we stop oscillator it automatically disconnects from outputs
      // so we guarantee that it will be cleared from memory
      this.oscillatorNode.stop();
      this.oscillatorNode = void 0;
    }
  }
}